import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  adeli_number: string | null;
  siret: string | null;
  subscription_status: string | null;
  subscription_end: string | null;
  analyses_this_month: number | null;
  role: string | null;
  created_at: string | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, profileData: Partial<Profile>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (err) {
        setError(err.message);
        setProfile(null);
      } else {
        setProfile(data as Profile | null);
        setError(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Custom lock function with timeout to prevent cross-tab freeze
    if (typeof navigator !== 'undefined' && navigator.locks) {
      try {
        navigator.locks.request('supabase_auth', { ifAvailable: true }, async () => {
          // pass
        });
      } catch {
        // pass
      }
    }

    const init = async () => {
      setLoading(true);
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        if (data.session?.user) {
          fetchProfile(data.session.user.id);
        }
      } catch (e) {
        if (mounted) {
          setError(e instanceof Error ? e.message : 'Erreur de session');
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setInitialized(true);
        }
      }
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) {
        setError(err.message);
      }
      return { error: err };
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Erreur de connexion');
      setError(err.message);
      return { error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, profileData: Partial<Profile>) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: profileData.first_name,
            last_name: profileData.last_name,
          },
        },
      });
      if (err) {
        setError(err.message);
        setLoading(false);
        return { error: err };
      }

      if (data.user) {
        const { error: profileErr } = await supabase.from('profiles').insert({
          id: data.user.id,
          first_name: profileData.first_name ?? null,
          last_name: profileData.last_name ?? null,
          adeli_number: profileData.adeli_number ?? null,
          siret: profileData.siret ?? null,
          subscription_status: 'active',
          subscription_end: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          analyses_this_month: 0,
          role: 'idel',
        });
        if (profileErr) {
          setError(profileErr.message);
          setLoading(false);
          return { error: new Error(profileErr.message) };
        }
        await fetchProfile(data.user.id);
      }

      return { error: null };
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Erreur d\'inscription');
      setError(err.message);
      return { error: err };
    } finally {
      setLoading(false);
    }
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setError(null);
    setLoading(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    initialized,
    error,
    signIn,
    signUp,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}